/**
 * Referential-integrity audit over a `DrawingDocument` (PR1).
 *
 * `validateDrawingReferences(document)` is a PURE function that walks every
 * cross-object reference in a drawing and returns a structured
 * `ReferenceAuditReport`. It NEVER mutates the document and NEVER throws —
 * a legal-but-dirty AutoCAD file (dangling layer / purged block / deleted
 * dimstyle) must still open, so the audit only REPORTS; healing is a
 * separate, additive pass (PR1b). `assertNoReferenceErrors(report)` gives
 * strict callers (CI, tests) the throw behaviour on demand.
 *
 * Scope (per the plan §4 46-field map):
 *   - Symbol-table name refs: layer, text/dimension/multileader style,
 *     block, annotation scale, LINETYPE (case-insensitive; dup names flagged).
 *   - Linetype refs (PR-LT2, §10.5): entity.linetype, layer.linetype,
 *     mleaderStyle.leaderLineType → linetypes (UNDEFINED_LINETYPE = 100,
 *     bypassing ByLayer/ByBlock/Continuous); complex-LTYPE text/shape element
 *     styleName → textStyles.
 *   - Handle refs: ownerHandle / ownerBlockRecordHandle / layoutHandle /
 *     annotativeDecompositions values / mleader attDefHandle.
 *   - Entity-id refs: array sourceEntityIds (per owning container).
 *   - Structural: duplicate entity ids per container, block-reference cycles.
 *
 * Intentionally OUT of scope (documented bypasses):
 *   - Hatch `patternName` — pattern library, not a document table.
 *   - Raw-group handle targets (representationDataHandle,
 *     extensionDictionaryHandle, extension dicts) — live only in raw groups,
 *     unverifiable.
 *   - xref `xrefPathName` — separate AUDIT/RECOVERALL axis.
 *   - Dynamic-block metadata refs (field 48) — deferred to PR2 (see TODO).
 *
 * MODULE LAYOUT (CLAUDE.md >1000-line split; barrel-preserved): the audit
 * implementation is split into package-private modules; this file keeps ONLY
 * the orchestrator + assert helper and re-exports the package-private pieces
 * the orchestrator wires together. The PUBLIC API (§5.2) is the two functions
 * below plus the five types in `referenceAudit.types.ts` — both surfaced via
 * `index.ts`.
 *   - referenceAudit.sentinels.ts   — SENT_NONE / SENT_ARROWHEAD
 *   - referenceAudit.resolution.ts  — Resolution / nameIndex / buildResolution
 *   - referenceAudit.checkers.ts    — checkName / checkHandle
 *   - referenceAudit.entityRefs.ts  — Container / checkAnnotativeContexts /
 *                                     checkEntityRefs
 *   - referenceAudit.structural.ts  — checkStyleTableRefs / checkHandleRefs /
 *                                     checkBlockReferenceCycles
 *
 * Source of truth: docs/superpowers/plans/2026-05-30-drawing-object-graph.md
 * §4 / §5.2 / §5.3 / §7 / §11 / §14.
 */

import type { DrawingDocument } from "./model";
import {
  buildResolution,
} from "./referenceAudit.resolution";
import {
  checkEntityRefs,
  checkRawEntityLinetypeRefs,
  type Container,
} from "./referenceAudit.entityRefs";
import {
  checkBlockReferenceCycles,
  checkHandleRefs,
  checkLinetypeStyleRefs,
  checkStyleTableRefs,
} from "./referenceAudit.structural";
import {
  ReferenceErrorCode,
  type ReferenceAuditReport,
  type ReferenceIssue,
} from "./referenceAudit.types";

/* ────────────────────────────────────────────────────────────────────────
 * Public API.
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * Audit every cross-object reference in `document`. PURE — never mutates
 * the input and NEVER throws (a dirty-but-legal file still produces a
 * report with `ok: false`). `fixes` is always `[]` here; healing lives in
 * `healDrawingReferences` (PR1b).
 */
export function validateDrawingReferences(
  document: DrawingDocument,
): ReferenceAuditReport {
  const errors: ReferenceIssue[] = [];

  // Build resolution indexes (also emits DUPLICATE_TABLE_ENTRY_NAME).
  const res = buildResolution(document, errors);

  // F5 — container-scoped: model/paper spaces AND every block definition.
  const containers: Container[] = [
    ...document.spaces.map(
      (s): Container => ({
        id: s.id,
        kind: "entity",
        entities: s.entities,
      }),
    ),
    ...Object.entries(document.blocks).map(
      ([name, b]): Container => ({
        id: name,
        kind: "blockChild",
        entities: b.entities,
      }),
    ),
  ];

  for (const c of containers) {
    const localIds = new Set<string>();
    const dupSeen = new Set<string>();
    for (const e of c.entities) {
      if (dupSeen.has(e.id)) {
        errors.push({
          code: ReferenceErrorCode.DUPLICATE_ENTITY_ID,
          ownerKind: c.kind,
          ownerId: e.id,
          container: c.id,
          path: "id",
          refKind: "entityId",
          field: "id",
          referencedKey: e.id,
          message: `duplicate entity id "${e.id}" in ${c.id}`,
        });
      }
      dupSeen.add(e.id);
      localIds.add(e.id);
    }
    for (const e of c.entities) {
      checkEntityRefs(e, c, localIds, res, errors);
    }
  }

  // FIX-2 (Codex PR-LT2 P2) — audit RAW (unsupported) entities' group-6
  // linetype refs too. The ENTITIES-section siblings live on
  // `document.rawEntityGroups` (model/paper space analogue); each block's
  // unsupported children live on `block.rawEntityGroups`. Without this scan a
  // raw entity carrying `6/<undefined>` would dangle silently (see
  // `checkRawEntityLinetypeRefs`). The space-level raw groups are document-wide
  // (the schema has no per-space `rawEntityGroups`), so attribute them to the
  // model space's container id when present, else the document.
  checkRawEntityLinetypeRefs(
    document.rawEntityGroups,
    res,
    "entity",
    document.spaces.find((s) => s.kind === "model")?.id ?? "document",
    errors,
  );
  for (const [name, b] of Object.entries(document.blocks)) {
    checkRawEntityLinetypeRefs(b.rawEntityGroups, res, "blockChild", name, errors);
  }

  checkStyleTableRefs(document, res, errors);
  checkHandleRefs(document, res, errors);
  checkLinetypeStyleRefs(document, res, errors);
  checkBlockReferenceCycles(document, errors);

  return { errors, fixes: [], ok: errors.length === 0 };
}

/**
 * LTYPE internal-consistency audit (PR-LT2). Re-exported here so the audit
 * surface is single-entry; the implementation lives in
 * `referenceAudit.linetypeFidelity.ts` (report-only, never throws, separate
 * from the cross-object reference pass — plan §10.2 / Codex#2 P2-7).
 */
export { auditLinetypeFidelity } from "./referenceAudit.linetypeFidelity";

/**
 * Throw iff the report has unresolved errors. Strict callers (CI / tests)
 * use this after `validateDrawingReferences` to convert the report model
 * into a throw model on demand.
 */
export function assertNoReferenceErrors(report: ReferenceAuditReport): void {
  if (report.ok) return;
  const summary = report.errors
    .map((e) => `[${ReferenceErrorCode[e.code]}] ${e.ownerId}: ${e.message}`)
    .join("; ");
  throw new Error(
    `Drawing has ${report.errors.length} reference error(s): ${summary}`,
  );
}
